import analytics from 'scenes/common/analytics';
import { ApiErrorReasonType } from 'scenes/common/types';
import {
  MotorSubSection,
  MotorFunnelState,
  MotorSection,
  ReferenceData,
  VehicleFromSearch,
  VehicleManuallyEntered,
} from 'scenes/motor/types';

import {
  marshalViewLoadedEvent,
  marshalSaveQuoteEvent,
  marshalEmailQuoteEvent,
  marshalResumeQuoteEvent,
  marshalRetrieveQuoteStartEvent,
  marshalRetrieveQuoteCompleteEvent,
  marshalFindCarEvent,
  marshalCarNotFoundEvent,
  marshalManualSearchEvent,
  marshalCarNotAvailableEvent,
  marshalAddDriverEvent,
  marshalOptionalExtrasEvent,
  marshalAddPolicyEvent,
  marshalQuoteReferred,
  marshalQuoteDeclined,
  marshalEditEvent,
  marshalFindOutMoreEvent,
  marshalCallUsEvent,
  marshalErrorEvent,
  marshalDriverAddedEvent,
  marshalSubmitQuoteEvent,
} from './marshaller';

const motorAnalytics = () => ({
  viewLoaded: (funnelState: MotorFunnelState, referenceData: ReferenceData) => {
    analytics().pushEvent(marshalViewLoadedEvent(funnelState, referenceData));
  },
  saveQuote: (funnelState: MotorFunnelState) => {
    analytics().pushEvent(marshalSaveQuoteEvent(funnelState));
  },
  emailQuote: (funnelState: MotorFunnelState) => {
    analytics().pushEvent(marshalEmailQuoteEvent(funnelState));
  },
  resumeQuote: (funnelState: MotorFunnelState) => {
    analytics().pushEvent(marshalResumeQuoteEvent(funnelState));
  },
  retrieveQuoteStart: (funnelState: MotorFunnelState) => {
    analytics().pushEvent(marshalRetrieveQuoteStartEvent(funnelState));
  },
  retrieveQuoteComplete: (funnelState: MotorFunnelState) => {
    analytics().pushEvent(marshalRetrieveQuoteCompleteEvent(funnelState));
  },
  findCar: (
    funnelState: MotorFunnelState,
    vehicle: VehicleFromSearch,
    referenceData: ReferenceData
  ) => {
    analytics().pushEvent(
      marshalFindCarEvent(funnelState, vehicle, referenceData)
    );
  },
  carNotFound: (funnelState: MotorFunnelState) => {
    analytics().pushEvent(marshalCarNotFoundEvent(funnelState));
  },
  manualSearch: (
    funnelState: MotorFunnelState,
    vehicle: VehicleManuallyEntered,
    referenceData: ReferenceData
  ) => {
    analytics().pushEvent(
      marshalManualSearchEvent(funnelState, vehicle, referenceData)
    );
  },
  carNotAvailable: (funnelState: MotorFunnelState) => {
    analytics().pushEvent(marshalCarNotAvailableEvent(funnelState));
  },
  addDriver: (funnelState: MotorFunnelState, referenceData: ReferenceData) => {
    analytics().pushEvent(marshalAddDriverEvent(funnelState, referenceData));
  },
  driverAdded: (
    funnelState: MotorFunnelState,
    referenceData: ReferenceData
  ) => {
    analytics().pushEvent(marshalDriverAddedEvent(funnelState, referenceData));
  },
  optionalExtras: (
    funnelState: MotorFunnelState,
    referenceData: ReferenceData
  ) => {
    analytics().pushEvent(
      marshalOptionalExtrasEvent(funnelState, referenceData)
    );
  },
  addPolicy: (funnelState: MotorFunnelState, referenceData: ReferenceData) => {
    analytics().pushEvent(marshalAddPolicyEvent(funnelState, referenceData));
  },
  referral: (
    funnelState: MotorFunnelState,
    quoteNumber: string,
    errorReason: ApiErrorReasonType,
    referenceData: ReferenceData
  ) => {
    if (errorReason === ApiErrorReasonType.Referral) {
      analytics().pushEvent(
        marshalQuoteReferred(funnelState, quoteNumber, referenceData)
      );
    } else if (errorReason === ApiErrorReasonType.Declined) {
      analytics().pushEvent(
        marshalQuoteDeclined(funnelState, quoteNumber, referenceData)
      );
    }
  },
  edit: (
    funnelState: MotorFunnelState,
    referenceData: ReferenceData,
    motorSection?: MotorSection,
    motorSubSection?: MotorSubSection
  ) => {
    analytics().pushEvent(
      marshalEditEvent(
        funnelState,
        referenceData,
        motorSection,
        motorSubSection
      )
    );
  },
  findOutMore: (funnelState: MotorFunnelState) => {
    analytics().pushEvent(marshalFindOutMoreEvent(funnelState));
  },
  callUs: (funnelState: MotorFunnelState) => {
    analytics().pushEvent(marshalCallUsEvent(funnelState));
  },
  error: (
    funnelState: MotorFunnelState,
    message: string = '',
    type: string = ApiErrorReasonType.ApiError,
    quoteNumber?: string
  ) => {
    analytics().pushEvent(
      marshalErrorEvent(funnelState, message, type, quoteNumber)
    );
  },
  submitQuote: (
    funnelState: MotorFunnelState,
    referenceData: ReferenceData
  ) => {
    analytics().pushEvent(marshalSubmitQuoteEvent(funnelState, referenceData));
  },
});

export default motorAnalytics;
